import * as React from 'react';
import {
  Avatar, Box, Button, Collapse, Dialog, DialogActions, DialogContent,
  Divider, InputAdornment, Stack, ThemeProvider, Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import LockResetIcon from '@mui/icons-material/LockReset';
import { grey } from '@mui/material/colors';

import theme from '../../themeContent';
import api from '../../services/api';
import CustomTextField from '../../components/CustomTextField';
import { useFormError } from '../../components/Datatable/FormErrorProvider';
import { useNotify } from '../../components/Datatable/NotifyProvider';

const EMPTY = {
  email: '', name: '', username: '',
  password: '', new_password: '', confirm_new_password: '',
};

export default function FormDialog(props) {
  const [state, setState] = React.useState(EMPTY);
  const [changePassword, setChangePassword] = React.useState(false);
  const error = useFormError();
  const notify = useNotify();

  const handleChange = (e) => setState((s) => ({ ...s, [e.target.name]: e.target.value }));

  React.useEffect(() => {
    if (!props.open) return;
    setChangePassword(false);
    error.setErrors([]);
    setState(EMPTY);
    api.get('/hermes/profile/')
      .then((res) => setState((s) => ({ ...s, ...res.data })))
      .catch(() => setState(EMPTY));
  }, [props.open]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (changePassword) {
      if (state.new_password !== state.confirm_new_password) {
        notify.setNotify({ open: true, message: 'As senhas não coincidem.', severity: 'error' });
        return;
      }
      if ((state.new_password || '').length < 8) {
        notify.setNotify({ open: true, message: 'A nova senha deve ter no mínimo 8 caracteres.', severity: 'error' });
        return;
      }
    }
    const payload = changePassword ? state : { name: state.name, email: state.email };
    api.put('/hermes/profile/', payload)
      .then((res) => {
        if (res?.data?.errors) {
          error.setErrors(res.data.errors);
          return;
        }
        notify.setNotify({ open: true, message: 'Perfil atualizado com sucesso.', severity: 'success' });
        props.handlerClose();
      })
      .catch(() => notify.setNotify({ open: true, message: 'Erro ao atualizar perfil.', severity: 'error' }));
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={props.open} onClose={props.handlerClose} fullWidth maxWidth="sm"
              PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Cabeçalho */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2.5,
                     bgcolor: 'primary.main', color: '#fff' }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,.18)', color: '#fff', width: 48, height: 48 }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {state.name || 'Meu perfil'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                @{state.username || '—'}
              </Typography>
            </Box>
          </Box>

          <DialogContent sx={{ bgcolor: grey[50], pt: 3 }}>
            <Typography variant="overline" color="text.secondary">Dados</Typography>
            <Stack spacing={2} sx={{ mt: 1, mb: 2 }}>
              <CustomTextField
                error={error.verify('name')} helperText={error.msgError('name')}
                size="small" required name="name" label="Nome" fullWidth
                value={state.name || ''} onChange={handleChange}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><BadgeIcon color="action" /></InputAdornment>
                ) }}
              />
              <CustomTextField
                disabled size="small" name="username" label="Usuário" fullWidth
                value={state.username || ''}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><PersonIcon color="action" /></InputAdornment>
                ) }}
              />
              <CustomTextField
                error={error.verify('email')} helperText={error.msgError('email')}
                size="small" name="email" label="E-mail" type="email" fullWidth
                value={state.email || ''} onChange={handleChange}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>
                ) }}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="overline" color="text.secondary">Segurança</Typography>
              {!changePassword && (
                <Button size="small" startIcon={<LockResetIcon />} onClick={() => setChangePassword(true)}>
                  Alterar senha
                </Button>
              )}
            </Box>

            <Collapse in={changePassword} unmountOnExit>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <CustomTextField
                  error={error.verify('password')} helperText={error.msgError('password')}
                  size="small" name="password" label="Senha atual" type="password" fullWidth
                  value={state.password || ''} onChange={handleChange}
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start"><LockIcon color="action" /></InputAdornment>
                  ) }}
                />
                <CustomTextField
                  error={error.verify('new_password')} helperText={error.msgError('new_password')}
                  size="small" name="new_password" label="Nova senha" type="password" fullWidth
                  value={state.new_password || ''} onChange={handleChange}
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start"><LockResetIcon color="action" /></InputAdornment>
                  ) }}
                />
                <CustomTextField
                  size="small" name="confirm_new_password" label="Confirmar nova senha" type="password" fullWidth
                  value={state.confirm_new_password || ''} onChange={handleChange}
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start"><LockResetIcon color="action" /></InputAdornment>
                  ) }}
                />
                <Button size="small" color="inherit" onClick={() => setChangePassword(false)} sx={{ alignSelf: 'flex-start' }}>
                  Cancelar alteração de senha
                </Button>
              </Stack>
            </Collapse>
          </DialogContent>

          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button variant="outlined" onClick={props.handlerClose}>Fechar</Button>
            <Button variant="contained" color="primary" type="submit">Salvar</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </ThemeProvider>
  );
}
